import React, { useState } from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { router } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { Visualiser } from "@/components/memory/Visualiser";
import { useThemeColor } from "@/hooks/useThemeColor";
import { MemoryModel, MediaModel, Contact, GroupModel } from "common247";

// Sample data exhibiting multi-duration and various rating levels
const SAMPLE_MEMORIES: MemoryModel[] = [
  {
    id: "mem-1",
    title: "Family Trip to Gotland",
    description: "Medieval week festival and limestone formations.",
    date: "01-Jul-2026 to 08-Jul-2026",
    contactIds: ["c-1", "c-2"],
    groupIds: ["g-1"],
    media: [
      {
        id: "med-1",
        name: "visby-walls.jpg",
        contentType: "image/jpeg",
        favouriteRating: 5,
      } as unknown as MediaModel,
    ],
  } as any,
  {
    id: "mem-2",
    title: "Canoeing on Lake Malaren",
    description: "Day excursion on clear waters.",
    date: "15-Jul-2026",
    contactIds: ["c-2"],
    groupIds: ["g-2"],
    media: [
      {
        id: "med-2",
        name: "lake-paddle.jpg",
        contentType: "image/jpeg",
        favouriteRating: 3,
      } as unknown as MediaModel,
    ],
  } as any,
  {
    id: "mem-3",
    title: "Nordic Lights Expedition",
    description: "Winter aurora borealis viewing and dogsledding.",
    date: "01-Dec-2026 to 05-Dec-2026",
    contactIds: ["c-1"],
    groupIds: ["g-1"],
    media: [
      {
        id: "med-3",
        name: "aurora.mp4",
        contentType: "video/mp4",
        favouriteRating: 4,
      } as unknown as MediaModel,
    ],
  } as any,
];

const SAMPLE_MEDIA: MediaModel[] = [
  {
    id: "med-1",
    name: "visby-walls.jpg",
    contentType: "image/jpeg",
    favouriteRating: 5,
  } as unknown as MediaModel,
  {
    id: "med-2",
    name: "lake-paddle.jpg",
    contentType: "image/jpeg",
    favouriteRating: 3,
  } as unknown as MediaModel,
  {
    id: "med-3",
    name: "aurora.mp4",
    contentType: "video/mp4",
    favouriteRating: 4,
  } as unknown as MediaModel,
];

const SAMPLE_CONTACTS: Contact[] = [
  { id: "c-1", name: "Astrid Lind", email: "astrid@example.com" } as any,
  { id: "c-2", name: "Lars Larsson", email: "lars@example.com" } as any,
];

const SAMPLE_GROUPS: GroupModel[] = [
  { id: "g-1", name: "Family Circle" } as any,
  { id: "g-2", name: "Outdoor Club" } as any,
];

/**
 * Interactive Design Workbench for Visualiser component.
 * Demonstrates D3 force simulation visualization with logarithmic duration scaling,
 * star-rating dot sizing, delta-time spacing, pan/zoom canvas, and export capabilities.
 */
export default function VisualiserDesignScreen(): React.JSX.Element {
  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({}, "textMuted");
  const badgeBg = useThemeColor({}, "buttonSecondary");

  const [selectedInfo, setSelectedInfo] = useState<string>("Tap any node to view details");

  return (
    <ThemedScreen
      title="Visualiser Design Sandbox"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/design" as any),
      }}
    >
      <ScrollView contentContainerStyle={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 8 }}>
          D3 RELATIONAL GRAPH WORKBENCH
        </ThemedText>

        <ThemedPanel padding={12} elevation="low" style={{ marginBottom: 12 }}>
          <View style={styles.headerRow}>
            <View style={[styles.iconBox, { backgroundColor: badgeBg }]}>
              <Ionicons name="git-network-outline" size={20} color={tintColor} />
            </View>
            <View style={{ flex: 1 }}>
              <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
                Visualiser Component
              </ThemedText>
              <ThemedText style={{ fontSize: 12, color: textMuted }}>
                Relational Canvas with D3 Force Simulation & Archival Heirloom Styling
              </ThemedText>
            </View>
          </View>
          <ThemedText style={styles.description}>
            Interactive test harness for Visualiser. Memory blocks scale with duration (days). Media dots
            scale with rating (1-5★). Inter-memory links scale with chronological day delta.
          </ThemedText>
        </ThemedPanel>

        <ThemedPanel padding={8} elevation="low" style={{ marginBottom: 12 }}>
          <ThemedText style={{ fontSize: 12, color: tintColor, fontWeight: "600" }}>
            Selection Inspector: {selectedInfo}
          </ThemedText>
        </ThemedPanel>

        {/* Live Visualiser Component */}
        <View style={styles.canvasContainer}>
          <Visualiser
            allMemories={SAMPLE_MEMORIES}
            allMedia={SAMPLE_MEDIA}
            allContacts={SAMPLE_CONTACTS}
            allGroups={SAMPLE_GROUPS}
            onSelectMemory={(m) => setSelectedInfo(`Selected Memory: ${m.title} (${m.date})`)}
            onSelectMedia={(med) => setSelectedInfo(`Selected Media: ${med.name || (med as any).filename} (${med.favouriteRating}★)`)}
            canvasWidth={900}
            canvasHeight={600}
          />
        </View>
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 8,
    paddingBottom: 32,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginBottom: 8,
  },
  iconBox: {
    width: 38,
    height: 38,
    borderRadius: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  description: {
    fontSize: 13,
    lineHeight: 18,
    opacity: 0.85,
  },
  canvasContainer: {
    minHeight: 650,
  },
});
